import type { IDIALOGVERIFYTRANSACTIONPROPS } from 'BreetComponents';
import { cleanRate, convertFxRate, type CURRENCYCODETYPE, getFxConversionType } from 'BreetHelpers';
import { useCurrencyOutput, useCurrentFxRate, useModalApi, useUserWalletApi } from 'BreetHooks';
import { useCallback, useMemo } from 'react';

import { fiatTransactionTypeObject, type IFiatTransactionDetailsProps } from './FiatTransactionDetails.helpers';
import Details from './FiatTransactionDetailsView';

const FiatTransactionDetails = ({ amount, fxRate, fromCurrencyCode, toCurrencyCode }: IFiatTransactionDetailsProps) => {
	const { openModalDirect } = useModalApi();

	const conversionType = getFxConversionType(fromCurrencyCode, toCurrencyCode);

	const transactionType = fiatTransactionTypeObject[conversionType];

	const userWalletProps = useUserWalletApi(fromCurrencyCode);

	const currentFxRate = useCurrentFxRate({ fxRate, conversionType });

	const currencyOutput = useCurrencyOutput();

	const expectedFiatAmount = useMemo(() => {
		if (!currentFxRate.rate) {
			console.error('No rate found');
			return '0';
		}

		const convertedAmount = convertFxRate(
			cleanRate(currentFxRate.rate),
			cleanRate(amount),
			fromCurrencyCode === 'USD' ? 'from' : 'to'
		);

		return currencyOutput(convertedAmount) ?? '0';
	}, [currencyOutput, currentFxRate.rate, amount, fromCurrencyCode]);

	const withdrawAmount = useMemo(() => currencyOutput(amount) ?? '0', [currencyOutput, amount]);

	const submitRequest = useCallback(() => {
		const metadata = {
			transactionType: transactionType === 'swap-currency' ? 'swapCurrency' : 'convertFiatToUSD',
			convertFiatToUSDReqData: {
				amount: Number(amount),
			},
			swapCurrencyReqData: {
				amount: Number(amount),
				from: fromCurrencyCode.toLowerCase() as Lowercase<CURRENCYCODETYPE>,
				to: toCurrencyCode.toLowerCase() as Lowercase<CURRENCYCODETYPE>,
			},
		} as IDIALOGVERIFYTRANSACTIONPROPS;

		openModalDirect({
			id: 'verifyTransaction',
			variant: 'right-sheet',
			metadata,
		});
	}, [amount, fromCurrencyCode, openModalDirect, toCurrencyCode, transactionType]);

	return (
		<Details
			amount={withdrawAmount}
			rate={currentFxRate.rateInfo}
			expectedFiat={{
				amount: `${currentFxRate.symbol.to}${expectedFiatAmount}`,
				currencyCode: toCurrencyCode,
			}}
			balance={{ amount: userWalletProps.currencyAmount, currencyCode: fromCurrencyCode }}
			handleVerification={submitRequest}
			symbol={currentFxRate.symbol.from}
		/>
	);
};

export default FiatTransactionDetails;
